import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';

interface PrayerSuggestion {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: string;
}

interface PrayerStreak {
  currentStreak: number;
  longestStreak: number;
  totalPrayers: number;
  lastPrayedAt?: Date;
}

interface SeasonalTheme {
  season: string;
  gradient: string;
  accent: string;
  message: string;
}

export function useSmartPrayerFeatures() {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<PrayerSuggestion[]>([]);
  const [streak, setStreak] = useState<PrayerStreak | null>(null);
  const [seasonalTheme, setSeasonalTheme] = useState<SeasonalTheme | null>(null);
  const [communityRequests, setCommunityRequests] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get intelligent prayer suggestions based on user activity
  const loadSuggestions = async () => {
    if (!user?.id) return;

    try {
      // Get recent prayer topics from user's prayer history
      const { data: recentPrayers } = await supabase
        .from('prayers')
        .select('category')
        .eq('author_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      // Get prayer analytics to understand patterns
      const { data: analytics } = await supabase
        .from('ai_prayer_analytics')
        .select('metadata')
        .eq('user_id', user.id)
        .order('timestamp', { ascending: false })
        .limit(20);

      // Generate intelligent suggestions based on patterns
      const categories = recentPrayers?.map(p => p.category).filter(Boolean) || [];
      const suggestions = generateSmartSuggestions(categories, analytics);
      setSuggestions(suggestions);
    } catch (error) {
      console.error('Failed to load prayer suggestions:', error);
    }
  };

  // Calculate prayer streak
  const loadPrayerStreak = async () => {
    if (!user?.id) return;

    try {
      const { data: prayerHistory } = await supabase
        .from('prayers')
        .select('created_at')
        .eq('author_id', user.id)
        .order('created_at', { ascending: false });

      if (prayerHistory) {
        const streak = calculatePrayerStreak(prayerHistory);
        setStreak(streak);
      }
    } catch (error) {
      console.error('Failed to load prayer streak:', error);
    }
  };

  // Get seasonal theme
  const loadSeasonalTheme = () => {
    const now = new Date();
    const theme = getSeasonalTheme(now);
    setSeasonalTheme(theme);
  };

  // Load community prayer requests
  const loadCommunityRequests = async () => {
    try {
      const { data: requests } = await supabase
        .from('prayers')
        .select('id, title, content, author_name, created_at')
        .eq('is_public', true)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(5);

      setCommunityRequests(requests || []);
    } catch (error) {
      console.error('Failed to load community requests:', error);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setIsLoading(true);
      await Promise.all([
        loadSuggestions(),
        loadPrayerStreak(),
        loadCommunityRequests()
      ]);
      loadSeasonalTheme();
      setIsLoading(false);
    };

    loadAll();
  }, [user?.id]);

  return {
    suggestions,
    streak,
    seasonalTheme,
    communityRequests,
    isLoading,
    refreshData: () => {
      loadSuggestions();
      loadPrayerStreak();
      loadCommunityRequests();
    }
  };
}

function generateSmartSuggestions(categories: string[], analytics: any[]): PrayerSuggestion[] {
  const timeOfDay = new Date().getHours();
  const dayOfWeek = new Date().getDay();
  
  const baseSuggestions: PrayerSuggestion[] = [
    { id: 'gratitude', title: 'Gratitude', description: 'Count your blessings', category: 'thanksgiving', icon: '🙏' },
    { id: 'guidance', title: 'Guidance', description: 'Seek divine direction', category: 'guidance', icon: '🧭' },
    { id: 'healing', title: 'Healing', description: 'Pray for restoration', category: 'healing', icon: '💚' },
    { id: 'peace', title: 'Peace', description: 'Find inner calm', category: 'peace', icon: '🕊️' },
    { id: 'strength', title: 'Strength', description: 'Draw from divine power', category: 'strength', icon: '💪' },
    { id: 'forgiveness', title: 'Forgiveness', description: 'Release and be freed', category: 'forgiveness', icon: '💝' }
  ];

  // Add time-specific suggestions
  if (timeOfDay < 10) {
    baseSuggestions.unshift({
      id: 'morning', title: 'Morning Prayer', description: 'Start your day with purpose', category: 'morning', icon: '🌅'
    });
  } else if (timeOfDay > 18) {
    baseSuggestions.unshift({
      id: 'evening', title: 'Evening Reflection', description: 'Reflect on your day', category: 'evening', icon: '🌙'
    });
  }

  // Add Sunday-specific suggestions
  if (dayOfWeek === 0) {
    baseSuggestions.unshift({
      id: 'worship', title: 'Worship & Praise', description: 'Honor the sacred day', category: 'worship', icon: '⛪'
    });
  }

  return baseSuggestions.slice(0, 6);
}

function calculatePrayerStreak(prayerHistory: any[]): PrayerStreak {
  if (!prayerHistory || prayerHistory.length === 0) {
    return { currentStreak: 0, longestStreak: 0, totalPrayers: 0 };
  }

  const dates = prayerHistory.map(p => new Date(p.created_at).toDateString());
  const uniqueDates = [...new Set(dates)].sort();
  
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 1;
  
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  
  // Check if user prayed today or yesterday for current streak
  if (uniqueDates.includes(today)) {
    currentStreak = 1;
    // Count consecutive days backward
    for (let i = uniqueDates.length - 2; i >= 0; i--) {
      const currentDate = new Date(uniqueDates[i + 1]);
      const previousDate = new Date(uniqueDates[i]);
      const dayDiff = (currentDate.getTime() - previousDate.getTime()) / (1000 * 3600 * 24);
      
      if (dayDiff === 1) {
        currentStreak++;
      } else {
        break;
      }
    }
  } else if (uniqueDates.includes(yesterday)) {
    currentStreak = 1;
    // Count consecutive days backward from yesterday
    for (let i = uniqueDates.length - 2; i >= 0; i--) {
      const currentDate = new Date(uniqueDates[i + 1]);
      const previousDate = new Date(uniqueDates[i]);
      const dayDiff = (currentDate.getTime() - previousDate.getTime()) / (1000 * 3600 * 24);
      
      if (dayDiff === 1) {
        currentStreak++;
      } else {
        break;
      }
    }
  }

  // Calculate longest streak
  for (let i = 1; i < uniqueDates.length; i++) {
    const currentDate = new Date(uniqueDates[i]);
    const previousDate = new Date(uniqueDates[i - 1]);
    const dayDiff = (currentDate.getTime() - previousDate.getTime()) / (1000 * 3600 * 24);
    
    if (dayDiff === 1) {
      tempStreak++;
    } else {
      longestStreak = Math.max(longestStreak, tempStreak);
      tempStreak = 1;
    }
  }
  longestStreak = Math.max(longestStreak, tempStreak);

  return {
    currentStreak,
    longestStreak,
    totalPrayers: prayerHistory.length,
    lastPrayedAt: prayerHistory[0] ? new Date(prayerHistory[0].created_at) : undefined
  };
}

function getSeasonalTheme(date: Date): SeasonalTheme {
  const month = date.getMonth();
  const day = date.getDate();

  // Christmas Season (December 1 - January 6)
  if ((month === 11 && day >= 1) || (month === 0 && day <= 6)) {
    return {
      season: 'Christmas',
      gradient: 'from-red-600 via-green-600 to-gold',
      accent: 'text-red-600',
      message: 'Celebrate the birth of our Savior'
    };
  }

  // Easter Season (March-April, simplified)
  if (month === 2 || month === 3) {
    return {
      season: 'Easter',
      gradient: 'from-purple-600 via-pink-500 to-yellow-400',
      accent: 'text-purple-600',
      message: 'Rejoice in the resurrection'
    };
  }

  // Advent (November 27 - December 24, simplified)
  if (month === 10 && day >= 27) {
    return {
      season: 'Advent',
      gradient: 'from-purple-700 via-blue-600 to-purple-500',
      accent: 'text-purple-700',
      message: 'Prepare your heart for His coming'
    };
  }

  // Lent (February-March, simplified)
  if (month === 1 || (month === 2 && day <= 15)) {
    return {
      season: 'Lent',
      gradient: 'from-purple-800 via-gray-600 to-purple-600',
      accent: 'text-purple-800',
      message: 'A time for reflection and repentance'
    };
  }

  // Default ordinary time
  return {
    season: 'Ordinary Time',
    gradient: 'from-prayer-primary via-prayer-secondary to-gold',
    accent: 'text-prayer-primary',
    message: 'Walk faithfully in His presence'
  };
}